import React from 'react';
import { useTranslation } from 'react-i18next';

import type { PrerequisiteInfo } from '../../../types/radar';
import PrerequisiteProgressBar from '../PrerequisiteProgressBar/PrerequisiteProgressBar';

import './_prerequisite-list.scss';

type PrerequisiteListProps = {
  onSelect?: (kcId: string) => void;
  prerequisites: PrerequisiteInfo[];
};

type PrerequisiteListItemProps = {
  onSelect?: (kcId: string) => void;
  prereq: PrerequisiteInfo;
};

function PrerequisiteListItem({ onSelect, prereq }: Readonly<PrerequisiteListItemProps>): JSX.Element {
  function handleClick(): void {
    if (onSelect) onSelect(prereq.kcId);
  }

  return (
    <li className="prerequisite-list__item">
      <PrerequisiteProgressBar
        currentKnowledge={prereq.currentKnowledge}
        onClick={onSelect ? handleClick : undefined}
        statement={prereq.statement}
        status={prereq.status}
      />
    </li>
  );
}

/**
 * PrerequisiteList - Renders a list of prerequisite KCs as progress bars,
 * with a shared descriptive header. When `onSelect` is provided, each item
 * becomes clickable and reports its kcId.
 */
function PrerequisiteList({ onSelect, prerequisites }: Readonly<PrerequisiteListProps>): JSX.Element | null {
  const { t } = useTranslation(['chatbot']);

  if (prerequisites.length === 0) return null;

  return (
    <div className="prerequisite-list">
      <p className="prerequisite-list__description" dangerouslySetInnerHTML={{ __html: t('chatbot:prerequisite_list_description') }} />
      <ul className="prerequisite-list__items">
        {prerequisites.map((prereq) => (
          <PrerequisiteListItem key={prereq.kcId} onSelect={onSelect} prereq={prereq} />
        ))}
      </ul>
    </div>
  );
}

export default PrerequisiteList;
